import { Link } from "@inertiajs/react"

import { Badge, StatusBadge } from "@/components/ui/badge"
import { Table, TableCard } from "@/components/ui/table"
import { adminEmailMessagePath } from "@/routes"

import {
  type EmailMessageSummary,
  OUTCOME_COLOR,
  OUTCOME_LABEL,
  ROUTE_COLOR,
  STATUS_COLOR,
  STATUS_LABEL,
  type WebhookEvent,
} from "../../messages/types"
import type { WebhookReport } from "../types"

export function RecentLedger({
  messages,
  ledgerHref,
}: {
  messages: EmailMessageSummary[]
  ledgerHref: string
}) {
  return (
    <TableCard.Root bleed>
      <TableCard.Caption
        title="Recent ledger"
        count={`${messages.length} newest`}
        trailing={
          <Link
            href={ledgerHref}
            className="text-2xs font-mono text-stone-50 underline underline-offset-[3px]"
          >
            Full ledger
          </Link>
        }
      />
      {messages.length === 0 ? (
        <TableCard.Empty>
          Nothing sent yet. The address check above works with an empty ledger.
        </TableCard.Empty>
      ) : (
        <Table aria-label="Recent ledger" rows="two-line">
          <Table.Header>
            <Table.Head
              id="recipient"
              isRowHeader
              label="Recipient"
              className="w-full"
            />
            <Table.Head id="status" label="Status" />
            <Table.Head id="route" label="Route" />
            <Table.Head id="sent" label="Created" align="right" sorted="desc" />
          </Table.Header>
          <Table.Body items={messages}>
            {(message) => (
              <Table.Row
                id={message.id}
                href={adminEmailMessagePath(message.id)}
              >
                <Table.Lead sub={message.mailer}>{message.to}</Table.Lead>
                <Table.Cell>
                  <StatusBadge tier={STATUS_COLOR[message.status]}>
                    {STATUS_LABEL[message.status]}
                  </StatusBadge>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tier={ROUTE_COLOR[message.route] ?? "neutral"}>
                    {message.route}
                  </StatusBadge>
                </Table.Cell>
                <Table.When at={message.created_at} relative />
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </TableCard.Root>
  )
}

export function RecentEvents({
  events,
  webhooks,
}: {
  events: WebhookEvent[]
  webhooks: WebhookReport
}) {
  return (
    <TableCard.Root bleed>
      <TableCard.Caption
        title="Provider events"
        count={`${events.length} latest`}
      />
      {events.length === 0 ? (
        <TableCard.Empty>
          No provider events recorded.{" "}
          {!webhooks.expected
            ? "Postmark cannot reach this environment, so none will arrive."
            : webhooks.never_received
              ? "See the webhook notice above."
              : `None in the retention window (${webhooks.retention_days} days).`}
        </TableCard.Empty>
      ) : (
        <Table aria-label="Latest provider events">
          <Table.Header>
            <Table.Head
              id="received"
              isRowHeader
              label="Received"
              align="right"
              sorted="desc"
            />
            <Table.Head id="type" label="Type" />
            <Table.Head id="outcome" label="Outcome" />
            <Table.Head
              id="description"
              label="Description"
              className="w-full"
            />
          </Table.Header>
          <Table.Body items={events}>
            {(event) => (
              <Table.Row id={event.id}>
                <Table.When at={event.received_at} relative />
                <Table.Cell>
                  <Badge type="modern" color="gray" size="sm">
                    {event.record_type}
                  </Badge>
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge tier={OUTCOME_COLOR[event.outcome]}>
                    {OUTCOME_LABEL[event.outcome]}
                  </StatusBadge>
                </Table.Cell>
                <Table.Cell className="max-w-xs truncate">
                  {event.description ?? "—"}
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </TableCard.Root>
  )
}
